import classNames from 'classnames';
import { FocusTrap } from 'focus-trap-react';
import { useCallback, useEffect, useState } from 'react';
import ModalPortal from '@alltrails/core/components/ModalPortal';
import useScreenLock from '@alltrails/core/hooks/useScreenLock';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import styles from './styles/styles.module.scss';

export type BottomSheetProps = {
  children: React.ReactNode;
  className?: string;
  isOpen?: boolean;
  onCloseRequest: () => void;
};

const BottomSheet = ({ children, className, isOpen = false, onCloseRequest }: BottomSheetProps) => {
  useScreenLock(isOpen);
  const [shouldRenderContent, setShouldRenderContent] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setShouldRenderContent(true);
    }
  }, [isOpen]);

  const onTransitionEnd = useCallback(() => {
    if (!isOpen) {
      setShouldRenderContent(false);
    }
  }, [isOpen]);

  return (
    <ModalPortal>
      <div {...setDenaliAttribute('BottomSheet')} className={classNames(styles.background, isOpen && styles.open)}>
        {/* onTransitionEnd on this div since its transition is longer than the background above */}
        <div className={classNames(styles.drawer, isOpen && styles.open)} onTransitionEnd={onTransitionEnd}>
          {/* Don't need to make the handle an actual button since the focus trap handles clicking on it */}
          <div className={styles.handleContainer}>
            <div className={styles.handle} />
          </div>
          {shouldRenderContent && (
            <FocusTrap
              active
              // fallbackFocus is used to prevent errors in testing https://github.com/focus-trap/focus-trap-react/issues/91
              focusTrapOptions={{
                clickOutsideDeactivates: true,
                escapeDeactivates: true,
                onDeactivate: onCloseRequest,
                fallbackFocus: '#bottom-sheet',
                initialFocus: false
              }}
            >
              <div className={classNames(styles.contents, className)} id="bottom-sheet">
                {children}
              </div>
            </FocusTrap>
          )}
        </div>
      </div>
    </ModalPortal>
  );
};

export default BottomSheet;
